import { useCallback, useMemo } from "react"
import type { Query as CubeQuery, TimeDimension } from "@cubejs-client/core"
import dayjs from "dayjs"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import { useCSVExportWorker } from "~/ui-rtk/hooks/use-csv-export-worker"
import { createSerializableColumnConfig } from "~/ui-rtk/utils/csv-export"

type CompareTimeDimension = TimeDimension & {
  compareDateRange?: Array<[string, string] | string[]>
}

/**
 * The query the worker runs for the CSV: the table's own request, untouched —
 * same members, filters, order, and the same page limit and offset, so the
 * file is the rows the table just loaded. When the table is comparing periods
 * its time dimension carries `compareDateRange: [[compare], [current]]` — one
 * Cube call answering with two result sets, which the worker does not read —
 * so that is split into a current-period query and a compare-period query,
 * each with a plain `dateRange` and everything else identical.
 */
export function splitExportQueries(tableQuery: CubeQuery): {
  query: CubeQuery
  compareQuery?: CubeQuery
} {
  const timeDimensions = (tableQuery.timeDimensions ??
    []) as CompareTimeDimension[]
  const comparing = timeDimensions.find(td => td.compareDateRange?.length === 2)
  if (!comparing) {
    return { query: tableQuery }
  }
  const withRange = (index: 0 | 1): CubeQuery => ({
    ...tableQuery,
    timeDimensions: timeDimensions.map(td => {
      if (!td.compareDateRange) return td
      const { compareDateRange, ...rest } = td
      return { ...rest, dateRange: compareDateRange[index] as [string, string] }
    }),
  })
  // compareDateRange is ordered [compare, current] by the connect hooks.
  return { query: withRange(1), compareQuery: withRange(0) }
}

/**
 * Export CSV for a funnel table through the Page Path report's CSV worker,
 * re-running the table's Cube request exactly as the page fired it (its
 * current page included), with the visible columns translated for the raw
 * rows (see buildFunnelExportColumns).
 */
export function useFunnelCsvExport({
  tableQuery,
  columns,
  filename,
}: {
  tableQuery: CubeQuery | undefined
  columns: TableVisualizationColumn[]
  filename: string
}) {
  const { exportToCSV, isExporting, progress, resetExport } =
    useCSVExportWorker()

  const queries = useMemo(
    () => (tableQuery ? splitExportQueries(tableQuery) : null),
    [tableQuery],
  )

  const exportCSV = useCallback(async () => {
    if (!queries || columns.length === 0) return
    resetExport()
    exportToCSV(queries.query, createSerializableColumnConfig(columns), {
      filename: `${filename}-${dayjs().format("YYYY-MM-DD")}.csv`,
      includeHeaders: true,
      compareQuery: queries.compareQuery,
      keepPagination: true,
    })
  }, [queries, columns, filename, exportToCSV, resetExport])

  // TableVisualization's exportProgress wants a string message; the worker
  // hook leaves it undefined between phases.
  const exportProgress = useMemo(
    () => ({ progress: progress.progress, message: progress.message ?? "" }),
    [progress.progress, progress.message],
  )

  return { exportCSV, isExporting, exportProgress }
}
